/*
 * Copyright 2024 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { foundryPlatformFetch as $foundryPlatformFetch } from "@osdk/shared.net.platformapi";
//
const _list = [0, "/v2/ontologies"];
/**
 * Lists the Ontologies visible to the current user.
 *
 * @public
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies
 */
export function list($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _list, ...args);
}
const _get = [0, "/v2/ontologies/{0}"];
/**
 * Gets a specific ontology for a given Ontology API name or RID.
 *
 * @public
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies/{ontology}
 */
export function get($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _get, ...args);
}
const _getFullMetadata = [0, "/v2/ontologies/{0}/fullMetadata", 2];
/**
 * Get the full Ontology metadata. This includes the objects, links, actions, queries, and interfaces.
 * This endpoint is designed to return as much metadata as possible in a single request to support OSDK workflows.
 * It may omit certain entities rather than fail the request.
 *
 * @beta
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies/{ontology}/fullMetadata
 */
export function getFullMetadata($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _getFullMetadata, ...args);
}
const _loadMetadata = [1, "/v2/ontologies/{0}/metadata", 3];
/**
 * Load Ontology metadata for the requested object, link, action, query, and interface types.
 *
 * @alpha
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies/{ontology}/metadata
 */
export function loadMetadata($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _loadMetadata, ...args);
}